import React from "react";
import { Clock, Calendar, DollarSign, User, FileText, PenTool, Tag, Globe, Printer } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { formatDate, formatCurrency } from "../../lib/contractUtils";

const SIG_BG = { fully_executed: "bg-emerald-100", awaiting_counter_signature: "bg-amber-100" };
const SIG_TEXT = { fully_executed: "text-emerald-600", awaiting_counter_signature: "text-amber-600" };

export function ContractSidebar({ contract }) {
  return (
    <div className="space-y-6">
      {/* Contract Details */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500">Contract Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <DetailRow icon={FileText} label="Type" value={contract.contract_type || "-"} />
          <DetailRow icon={DollarSign} label="Value" value={formatCurrency(contract.contract_value, contract.currency)} mono />
          <DetailRow icon={Calendar} label="Effective Date" value={formatDate(contract.effective_date)} />
          <DetailRow icon={Clock} label="Expiration Date" value={formatDate(contract.expiration_date)} />
          <DetailRow icon={User} label="Department" value={contract.department || "-"} />
          <DetailRow icon={Tag} label="Spend Category" value={contract.spend_category || "-"} />
          <DetailRow icon={Globe} label="Jurisdiction" value={contract.jurisdiction || "-"} />
        </CardContent>
      </Card>

      {/* Supplier */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500">Supplier</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <p className="text-xs text-slate-500 uppercase">Legal Name</p>
            <p className="font-medium">{contract.contracting_party?.legal_name || "-"}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase">Address</p>
            <p className="text-sm text-slate-600 dark:text-slate-400">{contract.contracting_party?.registered_address || "-"}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase">Contact Email</p>
            <p className="text-sm text-slate-600 dark:text-slate-400">{contract.contracting_party?.contact_email || "-"}</p>
          </div>
        </CardContent>
      </Card>

      {/* Signature Status */}
      {contract.signature_status && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500 flex items-center gap-2">
              Signature Status
              <span className="text-[10px] font-bold text-[#4C00FF] bg-[#4C00FF]/10 px-1.5 py-0.5 rounded">DocuSign</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded flex items-center justify-center ${SIG_BG[contract.signature_status] || "bg-[#4C00FF]/10"}`}>
                <PenTool size={18} className={SIG_TEXT[contract.signature_status] || "text-[#4C00FF]"} />
              </div>
              <div>
                <p className="font-medium capitalize text-sm">
                  {contract.signature_status === "awaiting_internal" && "Awaiting Your Signature"}
                  {contract.signature_status === "awaiting_counter_signature" && "Awaiting Supplier Signature"}
                  {contract.signature_status === "fully_executed" && "Fully Executed"}
                </p>
              </div>
            </div>
            {contract.docusign_envelope_id && (
              <div className="text-xs text-slate-400 font-mono">
                Envelope: {contract.docusign_envelope_id}
              </div>
            )}
            {contract.internal_signed_at && (
              <div className="text-xs text-slate-500">
                <p>Internal signed: {formatDate(contract.internal_signed_at)}</p>
              </div>
            )}
            {contract.supplier_signed_at && (
              <div className="text-xs text-slate-500">
                <p>Supplier signed: {formatDate(contract.supplier_signed_at)} by {contract.supplier_signed_by}</p>
              </div>
            )}
            {contract.draft_shared_at && (
              <div className="text-xs text-slate-500 pt-1 border-t">
                <p>Draft shared: {formatDate(contract.draft_shared_at)}</p>
                <p>To: {contract.draft_shared_to}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Print & Sign Status */}
      {contract.print_sign_status && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500 flex items-center gap-2">
              Print & Sign
              <span className="text-[10px] font-bold text-cyan-600 bg-cyan-100 px-1.5 py-0.5 rounded">Wet Ink</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded flex items-center justify-center ${
                contract.print_sign_status === "executed" ? "bg-emerald-100" : "bg-cyan-100"
              }`}>
                <Printer size={18} className={
                  contract.print_sign_status === "executed" ? "text-emerald-600" : "text-cyan-600"
                } />
              </div>
              <div>
                <p className="font-medium text-sm">
                  {contract.print_sign_status === "pdf_generated" && "PDF Generated — Awaiting Upload"}
                  {contract.print_sign_status === "executed" && "Executed (Wet-Ink Signed)"}
                </p>
              </div>
            </div>
            {contract.print_sign_generated_at && (
              <div className="text-xs text-slate-500">
                <p>PDF generated: {formatDate(contract.print_sign_generated_at)}</p>
              </div>
            )}
            {contract.print_sign_executed_at && (
              <div className="text-xs text-slate-500">
                <p>Signed copy uploaded: {formatDate(contract.print_sign_executed_at)}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DetailRow({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
        <Icon size={16} className="text-slate-500" />
      </div>
      <div>
        <p className="text-xs text-slate-500 uppercase">{label}</p>
        <p className={`font-medium ${mono ? "font-mono" : ""}`}>{value}</p>
      </div>
    </div>
  );
}
